/**
 * Job List Filter Module
 * Filters job items based on company, location, category, working time, job type, and search text
 */
export class JobListFilter {
  constructor(containerSelector = '.hr-jobs-list') {
    this.container = document.querySelector(containerSelector);

    if (!this.container) {
      return;
    }

    this.companyFilter = this.container.querySelector('#companyFilter');
    this.locationFilter = this.container.querySelector('#locationFilter');
    this.categoryFilter = this.container.querySelector('#categoryFilter');
    this.fulltimeCheckbox = this.container.querySelector('#fulltime');
    this.parttimeCheckbox = this.container.querySelector('#parttime');
    this.kaufCheckbox = this.container.querySelector('#kauf');
    this.gewerbCheckbox = this.container.querySelector('#gewerb');
    this.searchInput = this.container.querySelector('#search');
    this.jobItems = this.container.querySelectorAll('.job-item');

    this.init();
  }

  init() {
    // Bind event listeners
    this.companyFilter?.addEventListener('change', () => this.filterJobs());
    this.locationFilter?.addEventListener('change', () => this.filterJobs());
    this.categoryFilter?.addEventListener('change', () => this.filterJobs());
    this.fulltimeCheckbox?.addEventListener('change', () => this.filterJobs());
    this.parttimeCheckbox?.addEventListener('change', () => this.filterJobs());
    this.kaufCheckbox?.addEventListener('change', () => this.filterJobs());
    this.gewerbCheckbox?.addEventListener('change', () => this.filterJobs());
    this.searchInput?.addEventListener('input', () => this.filterJobs());
  }

  filterJobs() {
    const selectedCompany = this.companyFilter?.value || '';
    const selectedLocation = this.locationFilter?.value || '';
    const selectedCategory = this.categoryFilter?.value || '';
    const fulltimeChecked = this.fulltimeCheckbox?.checked || false;
    const parttimeChecked = this.parttimeCheckbox?.checked || false;
    const kaufChecked = this.kaufCheckbox?.checked || false;
    const gewerbChecked = this.gewerbCheckbox?.checked || false;
    const searchTerm = this.searchInput?.value.toLowerCase().trim() || '';

    this.jobItems.forEach(jobItem => {
      let isVisible = true;

      // Filter by company
      if (selectedCompany) {
        const companies = jobItem.dataset.companies?.split(',') || [];
        if (!companies.includes(selectedCompany)) {
          isVisible = false;
        }
      }

      // Filter by location
      if (selectedLocation) {
        const locations = jobItem.dataset.locations?.split(',') || [];
        if (!locations.includes(selectedLocation)) {
          isVisible = false;
        }
      }

      // Filter by category
      if (selectedCategory) {
        const categories = jobItem.dataset.categories?.split(',') || [];
        if (!categories.includes(selectedCategory)) {
          isVisible = false;
        }
      }

      // Filter by working time
      if (fulltimeChecked || parttimeChecked) {
        const workingTime = jobItem.dataset.workingTime || '';
        const workingTimes = workingTime.split(',').map(t => t.trim());

        let workingTimeMatch = false;
        if (fulltimeChecked && workingTimes.includes('fulltime')) {
          workingTimeMatch = true;
        }
        if (parttimeChecked && workingTimes.includes('parttime')) {
          workingTimeMatch = true;
        }

        if (!workingTimeMatch) {
          isVisible = false;
        }
      }

      // Filter by job type (kaufmännisch/gewerblich)
      if (kaufChecked || gewerbChecked) {
        const jobType = jobItem.dataset.jobType || '';
        const jobTypes = jobType.split(',').map(t => t.trim());

        let jobTypeMatch = false;
        if (kaufChecked && jobTypes.includes('kaufmännisch')) {
          jobTypeMatch = true;
        }
        if (gewerbChecked && jobTypes.includes('gewerblich')) {
          jobTypeMatch = true;
        }

        if (!jobTypeMatch) {
          isVisible = false;
        }
      }

      // Filter by search term
      if (searchTerm) {
        const jobText = jobItem.textContent.toLowerCase();
        const additionalSearchText = jobItem.dataset.additionalSearchText.toLowerCase();
        console.log(additionalSearchText);
        if (!jobText.includes(searchTerm) && !additionalSearchText.includes(searchTerm)) {
          isVisible = false;
        }
      }

      // Show or hide job item
      if (isVisible) {
        jobItem.style.display = '';
        jobItem.classList.remove('hidden');
      } else {
        jobItem.style.display = 'none';
        jobItem.classList.add('hidden');
      }
    });

    // Optional: Show message if no results
    this.updateNoResultsMessage();
  }

  updateNoResultsMessage() {
    const visibleJobs = Array.from(this.jobItems).filter(
      item => item.style.display !== 'none'
    );

    let noResultsMsg = this.container.querySelector('.no-results-message');

    if (visibleJobs.length === 0) {
      if (!noResultsMsg) {
        noResultsMsg = document.createElement('div');
        noResultsMsg.className = 'no-results-message';
        noResultsMsg.textContent = 'Keine Stellenangebote gefunden.';
        this.container.querySelector('.hr-jobs-list-jobs')?.appendChild(noResultsMsg);
      }
    } else {
      noResultsMsg?.remove();
    }
  }

  reset() {
    if (this.companyFilter) this.companyFilter.value = '';
    if (this.locationFilter) this.locationFilter.value = '';
    if (this.categoryFilter) this.categoryFilter.value = '';
    if (this.fulltimeCheckbox) this.fulltimeCheckbox.checked = false;
    if (this.parttimeCheckbox) this.parttimeCheckbox.checked = false;
    if (this.kaufCheckbox) this.kaufCheckbox.checked = false;
    if (this.gewerbCheckbox) this.gewerbCheckbox.checked = false;
    if (this.searchInput) this.searchInput.value = '';

    this.filterJobs();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new JobListFilter();
  });
} else {
  new JobListFilter();
}

// Export for manual initialization
export default JobListFilter;
